/**
 * Salon booking page — new beauty/barber flow under `/book/:salonSlug`.
 *
 * Built specifically for single-salon, end-customer-facing bookings (the
 * kind of link a salon posts in their Instagram bio). Designed against
 * alteg.io / Booksy as the comparison set, but tuned for Armenian copy
 * length and the project's existing visual language.
 *
 * The page itself is intentionally thin — almost all interactive state
 * lives in `SalonBookingContext`, while data fetching is a single call
 * into the mock-data module today. When the backend ships, swap the
 * `getSalon` / `getServiceCategories` calls for `await fetchSalon(slug)`
 * and friends without touching the layout.
 */

import { useCallback, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useTheme } from "../components/ThemeProvider";
import { SalonBookingProvider, useSalonBooking } from "../components/salonBooking/SalonBookingContext";
import {
  createMockBooking,
  generateAnyMasterAvailability,
  generateAvailability,
  getMasters,
  getSalon,
  getServiceBundles,
  getServiceCategories,
  getMasterById,
  getMastersForServices,
} from "../components/salonBooking/mockSalonData";
import { getSalonPalette, pickLocalized } from "../components/salonBooking/salonTheme";
import { SalonHero } from "../components/salonBooking/SalonHero";
import { ServiceCatalog } from "../components/salonBooking/ServiceCatalog";
import { BookingCart } from "../components/salonBooking/BookingCart";
import { CheckoutDrawer } from "../components/salonBooking/CheckoutDrawer";
import { SalonSuccessModal } from "../components/salonBooking/SalonSuccessModal";
import { MasterPicker } from "../components/salonBooking/MasterPicker";
import { DateTimePicker } from "../components/salonBooking/DateTimePicker";

const START_SERVICE = "service";
const START_MASTER = "master";
const START_TIME = "time";

function StartChoiceCard({ palette, icon, title, subtitle, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-start w-100"
      style={{
        background: palette.surface,
        border: palette.border,
        borderRadius: 16,
        padding: "16px 18px",
        color: palette.text,
        display: "flex",
        alignItems: "center",
        gap: 14,
        cursor: "pointer",
        transition: "background 0.15s ease, border-color 0.15s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = palette.surfaceAlt;
        e.currentTarget.style.border = `1px solid ${palette.accentBorder}`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = palette.surface;
        e.currentTarget.style.border = palette.border;
      }}
    >
      <span
        style={{
          width: 48,
          height: 48,
          borderRadius: "50%",
          background: palette.surfaceAlt,
          color: palette.accent,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "1.35rem",
          flexShrink: 0,
        }}
      >
        <i className={icon} aria-hidden="true" />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span className="fw-700 d-block" style={{ fontSize: "1rem", color: palette.text }}>
          {title}
        </span>
        <span style={{ color: palette.textMuted, fontSize: "0.86rem", lineHeight: 1.4 }}>
          {subtitle}
        </span>
      </span>
      <span style={{ color: palette.textSubtle, fontSize: "1.2rem" }}>
        <i className="las la-angle-right" aria-hidden="true" />
      </span>
    </button>
  );
}

function StartPanel({
  palette,
  lang,
  salon,
  activeMode,
  onChoose,
  onBack,
  onContinue,
}) {
  const { t } = useTranslation();
  const { masterId, setMaster, slot, setSlot } = useSalonBooking();

  const days = useMemo(() => {
    if (masterId && masterId !== "any") {
      return generateAvailability({ masterId, totalDuration: 30 });
    }
    return generateAnyMasterAvailability({
      serviceIds: [],
      totalDuration: 30,
    });
  }, [masterId]);

  const selectedMaster = masterId && masterId !== "any" ? getMasterById(masterId) : null;

  if (!activeMode) {
    return (
      <div
        style={{
          background: palette.surface,
          border: palette.border,
          borderRadius: 18,
          padding: "clamp(18px, 4vw, 26px)",
          boxShadow: palette.shadowSoft,
        }}
      >
        <div className="mb-3">
          <h2
            className="fw-800 mb-1"
            style={{
              color: palette.text,
              fontSize: "clamp(1.25rem, 3vw, 1.7rem)",
              lineHeight: 1.2,
            }}
          >
            {pickLocalized(salon.name, lang)}
          </h2>
          <div style={{ color: palette.textMuted, fontSize: "0.95rem" }}>
            {pickLocalized(salon.address, lang)}
          </div>
        </div>
        <div style={{ display: "grid", gap: 12 }}>
          <StartChoiceCard
            palette={palette}
            icon="las la-user-friends"
            title={t("salonBooking.start.masterTitle")}
            subtitle={t("salonBooking.start.masterSubtitle")}
            onClick={() => onChoose(START_MASTER)}
          />
          <StartChoiceCard
            palette={palette}
            icon="las la-calendar"
            title={t("salonBooking.start.timeTitle")}
            subtitle={t("salonBooking.start.timeSubtitle")}
            onClick={() => onChoose(START_TIME)}
          />
          <StartChoiceCard
            palette={palette}
            icon="las la-list-ul"
            title={t("salonBooking.start.serviceTitle")}
            subtitle={t("salonBooking.start.serviceSubtitle")}
            onClick={() => onChoose(START_SERVICE)}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        background: palette.surface,
        border: palette.border,
        borderRadius: 18,
        padding: "clamp(18px, 4vw, 26px)",
        boxShadow: palette.shadowSoft,
      }}
    >
      <div
        className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-3"
      >
        <div>
          <h2
            className="fw-800 mb-1"
            style={{
              color: palette.text,
              fontSize: "clamp(1.15rem, 3vw, 1.45rem)",
              lineHeight: 1.2,
            }}
          >
            {activeMode === START_MASTER
              ? t("salonBooking.start.masterTitle")
              : t("salonBooking.start.timeTitle")}
          </h2>
          <p className="mb-0" style={{ color: palette.textMuted, fontSize: "0.92rem" }}>
            {activeMode === START_MASTER
              ? t("salonBooking.start.masterLead")
              : t("salonBooking.start.timeLead")}
          </p>
        </div>
        <button
          type="button"
          onClick={onBack}
          className="pill fw-600"
          style={{
            background: palette.surfaceAlt,
            border: palette.border,
            color: palette.textMuted,
            padding: "8px 14px",
          }}
        >
          {t("salonBooking.start.changeStart")}
        </button>
      </div>

      {activeMode === START_MASTER ? (
        <MasterPicker
          palette={palette}
          lang={lang}
          masters={getMasters()}
          selectedId={masterId}
          onSelect={setMaster}
        />
      ) : (
        <DateTimePicker
          palette={palette}
          lang={lang}
          days={days}
          value={slot}
          onChange={setSlot}
        />
      )}

      <div
        className="d-flex flex-wrap align-items-center justify-content-between gap-2"
        style={{ marginTop: 16 }}
      >
        <div style={{ color: palette.textMuted, fontSize: "0.86rem" }}>
          {activeMode === START_MASTER && selectedMaster
            ? t("salonBooking.start.selectedMaster", {
                name: pickLocalized(selectedMaster.name, lang),
              })
            : activeMode === START_TIME && slot
              ? t("salonBooking.start.selectedTime", {
                  date: slot.date,
                  time: slot.time,
                })
              : t("salonBooking.start.nextHint")}
        </div>
        <button
          type="button"
          onClick={onContinue}
          className="pill fw-700"
          style={{
            background: palette.brandGradient,
            border: "none",
            color: "#fff",
            padding: "10px 18px",
          }}
        >
          {t("salonBooking.start.continueServices")}
          <i className="las la-arrow-right ms-2" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

function SalonBookingInner({ salon, palette, categories, bundles }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const servicesRef = useRef(null);
  const {
    cart,
    totalDuration,
    totalPrice,
    hasOpenPriceFrom,
    slot,
    masterId,
    client,
    booking,
    bookingState,
    startBooking,
    succeedBooking,
    failBooking,
    resetForNewBooking,
  } = useSalonBooking();

  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [startMode, setStartMode] = useState(null);

  const scrollToServices = useCallback(() => {
    window.setTimeout(() => {
      servicesRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 40);
  }, []);

  const handleStartChoose = useCallback(
    (mode) => {
      if (mode === START_SERVICE) {
        scrollToServices();
        return;
      }
      setStartMode(mode);
    },
    [scrollToServices],
  );

  const handleStartContinue = useCallback(() => {
    scrollToServices();
  }, [scrollToServices]);

  /**
   * Cart eligibility check passed down to the catalog so we can warn the
   * user when a service in a category couldn't be combined with the
   * current cart (no single master can deliver everything). Used as a
   * visual hint only — selection itself is never blocked.
   */
  const eligibilityForCategory = useCallback(
    (category) => {
      if (cart.length === 0) return true;
      const ids = cart.map((c) => c.id);
      const masters = getMastersForServices(ids);
      if (masters.length === 0) return true;
      return category.services.some((svc) => {
        if (!svc.masterIds || svc.masterIds.length === 0) return true;
        return masters.some((m) => svc.masterIds.includes(m.id));
      });
    },
    [cart],
  );

  const handleContinue = useCallback(() => {
    if (cart.length === 0) return;
    setCheckoutOpen(true);
  }, [cart.length]);

  const handleConfirm = useCallback(async () => {
    startBooking();
    try {
      const result = await createMockBooking({
        salonSlug: salon.slug,
        services: cart.map((s) => ({
          id: s.id,
          name: pickLocalized(s.name, lang),
          duration: s.duration,
          price: s.price ?? s.priceFrom,
        })),
        masterId: slot?.masterId || masterId,
        date: slot?.date,
        time: slot?.time,
        client,
        totalPrice,
        totalDuration,
      });
      succeedBooking(result);
      setCheckoutOpen(false);
    } catch (err) {
      console.error("[salon-booking] confirm failed", err);
      failBooking(err);
    }
  }, [
    startBooking,
    succeedBooking,
    failBooking,
    salon,
    cart,
    slot,
    masterId,
    client,
    totalPrice,
    totalDuration,
    lang,
  ]);

  const showSuccess = bookingState === "success" && Boolean(booking);

  return (
    <div style={{ background: palette.pageBg, minHeight: "100vh" }}>
      <SalonHero palette={palette} salon={salon} lang={lang} />

      <section
        style={{
          padding: "clamp(20px, 4vw, 36px) 0 clamp(120px, 16vw, 180px)",
        }}
      >
        <div className="container container-two">
          <div style={{ marginBottom: "clamp(18px, 4vw, 28px)" }}>
            <StartPanel
              palette={palette}
              lang={lang}
              salon={salon}
              activeMode={startMode}
              onChoose={handleStartChoose}
              onBack={() => setStartMode(null)}
              onContinue={handleStartContinue}
            />
          </div>

          <div ref={servicesRef} />
          <div className="row g-4">
            <div className="col-12 col-lg-8">
              <div
                className="d-flex flex-wrap align-items-center justify-content-between mb-3"
                style={{ gap: 10 }}
              >
                <div>
                  <h2
                    className="fw-700 mb-1"
                    style={{
                      color: palette.text,
                      fontSize: "clamp(1.2rem, 3vw, 1.6rem)",
                      lineHeight: 1.2,
                    }}
                  >
                    {t("salonBooking.catalog.title")}
                  </h2>
                  <p
                    className="mb-0"
                    style={{
                      color: palette.textMuted,
                      fontSize: "clamp(0.9rem, 2vw, 1rem)",
                    }}
                  >
                    {t("salonBooking.catalog.subtitle")}
                  </p>
                </div>
                <span
                  className="d-inline-flex align-items-center gap-1"
                  style={{
                    background: palette.successSoft,
                    color: palette.success,
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    padding: "5px 12px",
                    borderRadius: 999,
                  }}
                >
                  <i className="las la-bolt" aria-hidden="true" />
                  {t("salonBooking.catalog.realtimeBadge")}
                </span>
              </div>
              <ServiceCatalog
                palette={palette}
                lang={lang}
                categories={categories}
                bundles={bundles}
                isCartEligible={eligibilityForCategory}
              />
            </div>
            <div className="col-12 col-lg-4">
              <BookingCart
                palette={palette}
                lang={lang}
                onContinue={handleContinue}
              />
            </div>
          </div>
        </div>
      </section>

      <CheckoutDrawer
        palette={palette}
        lang={lang}
        isOpen={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        onConfirm={handleConfirm}
      />

      <SalonSuccessModal
        palette={palette}
        lang={lang}
        isOpen={showSuccess}
        onClose={resetForNewBooking}
        booking={booking}
        cart={cart}
        totalDuration={totalDuration}
        totalPrice={totalPrice}
        hasOpenPriceFrom={hasOpenPriceFrom}
        slot={slot}
        masterId={slot?.masterId || masterId}
        salon={salon}
        onNewBooking={resetForNewBooking}
      />
    </div>
  );
}

const SalonBookingPage = () => {
  const { t } = useTranslation();
  const { salonSlug } = useParams();
  const { isDarkMode } = useTheme();
  const isVisuallyDark = !isDarkMode;

  /* If the URL doesn't carry a slug (the bare `/book` route) we default
     to the demo salon — keeps the link the director can hand around
     painless even when there's only one tenant in the system. */
  const slug = salonSlug || "demo";

  const salon = useMemo(() => getSalon(slug), [slug]);
  const categories = useMemo(() => getServiceCategories(), []);
  const bundles = useMemo(() => getServiceBundles(), []);

  const palette = useMemo(
    () =>
      getSalonPalette(
        isVisuallyDark,
        salon?.brandColor,
        salon?.brandColorSoft,
      ),
    [isVisuallyDark, salon],
  );

  if (!salon) {
    return (
      <div style={{ background: palette.pageBg, minHeight: "60vh" }}>
        <div className="container container-two" style={{ padding: "80px 0" }}>
          <div
            style={{
              background: palette.surface,
              border: palette.border,
              borderRadius: 18,
              padding: "40px 24px",
              textAlign: "center",
              maxWidth: 520,
              margin: "0 auto",
            }}
          >
            <i
              className="las la-store-slash"
              style={{
                fontSize: "3rem",
                color: palette.accent,
                display: "block",
                marginBottom: 10,
              }}
              aria-hidden="true"
            />
            <h2
              className="fw-700 mb-2"
              style={{ color: palette.text, fontSize: "1.4rem" }}
            >
              {t("salonBooking.notFound.title")}
            </h2>
            <p style={{ color: palette.textMuted, marginBottom: 0 }}>
              {t("salonBooking.notFound.subtitle", { slug })}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <SalonBookingProvider>
      <SalonBookingInner
        salon={salon}
        palette={palette}
        categories={categories}
        bundles={bundles}
      />
    </SalonBookingProvider>
  );
};

export default SalonBookingPage;
